import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { Column } from "component/shared/table";
import { useSelector } from "react-redux";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { RefreshTable } from "component/shared/refresh-table";
import { RootState } from "redux/store";
import { ClientBoardResource } from "api/resource/client-board";
import { AgentMlsIds } from "model/agent-mls-ids";
import { plainRows } from "../featured-table";
import { BoardCell } from "../board-cell";
import { OfficeIdHeader } from "../office-id-header";
import { styles } from "./style";

export type PlainKind = "office" | "singleAgent";

interface Props extends WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	// An office account's rows are Office MLS IDs. A single-agent account's are the agent's MLS ID with its Backup
	// Office MLS ID.
	kind: PlainKind;
	onEdit: (row: ClientBoardResource) => void;
	onRemove: (row: ClientBoardResource) => void;
}

export const MLS_ID_LABEL: Record<PlainKind, string> = { office: "Office MLS ID", singleAgent: "Agent MLS ID" };

// The client-board rows, by board then ID, each with a plain edit and remove. No agent rows, states or fixes.
const Component: React.FC<Props> = ({ agentMlsIds, kind, onEdit, onRemove, classes = {} }) => {
	const userId = useSelector((state: RootState) => state.authentication.user?.agentId);
	const boards = agentMlsIds.boards;
	const rows = plainRows(agentMlsIds);
	if (rows.length === 0) {
		return <Mui.Typography className={classes.empty}>No MLS IDs added yet.</Mui.Typography>;
	}
	const columns: Column<ClientBoardResource>[] = [
		{ id: "board", title: "MLS Board", value: (row) => <BoardCell board={boards.find((b) => b.boardId === row.boardId)} fallback={row.boardName} /> },
		{ id: "mlsId", title: MLS_ID_LABEL[kind], value: (row) => row.agentId },
		{ id: "alternateId", title: <OfficeIdHeader />, hidden: kind === "office", value: (row) => row.officeId || "None" },
		{
			id: "action",
			title: "Action",
			align: "right",
			value: (row) => (
				<div className={classes.actions}>
					<Mui.IconButton aria-label="Edit" title="Edit" onClick={() => onEdit(row)}>
						<Icons.Edit size={20} />
					</Mui.IconButton>
					<Mui.IconButton aria-label="Remove" title="Remove" onClick={() => onRemove(row)}>
						<Icons.Trash size={20} />
					</Mui.IconButton>
				</div>
			),
		},
	];
	return (
		<RefreshTable
			items={rows}
			columns={columns}
			// The rows come in the page's order; no column sorts them.
			sort={{ id: "", desc: false }}
			rowKey={(row) => row.id}
			userId={userId}
			tableKey="featured-mls-ids-plain"
		/>
	);
};

export const FeaturedPlainTable = withStyles(Component, styles);
